import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Loader2, RefreshCw, Download, Copy, ChevronDown, ChevronRight,
  Search, Globe, Target, Users, Zap, AlertTriangle, ExternalLink,
  Lightbulb, MapPin, Hash, BookOpen, Calendar, Star, Shield, Coffee
} from 'lucide-react';
import { toast } from 'sonner';
import {
  generateSourcingPlan, getSourcingJobStatus, getSourcingReportByJob
} from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRESS_MESSAGES = [
  "Mapping talent pools and sourcing channels...",
  "Scanning professional networks and alumni groups...",
  "Building Boolean and X-ray search strings...",
  "Identifying local communities and meetups...",
  "Analyzing social media sourcing opportunities...",
  "Discovering hidden talent channels...",
  "Compiling niche communities and job boards...",
  "Generating Week 1 action plan...",
  "Finalizing your sourcing strategy...",
];

const CopyBtn = ({ text, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 shrink-0" onClick={handleCopy} data-testid="sourcing-copy-btn">
      <Copy className="h-3 w-3" /> {copied ? 'Copied' : label}
    </Button>
  );
};

const DifficultyBadge = ({ rating }) => {
  const colors = {
    'Easy': 'bg-green-500', 'Moderate': 'bg-amber-500',
    'Challenging': 'bg-orange-500', 'Very Difficult': 'bg-red-500'
  };
  return <Badge className={`${colors[rating] || 'bg-slate-500'} text-white text-xs`}>{rating}</Badge>;
};

const CostBadge = ({ cost }) => (
  <Badge className={`${cost === 'Free' ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-amber-500/10 text-amber-600 border-amber-500/20'} text-[10px]`}>
    {cost}
  </Badge>
);

const SearchBlock = ({ str, label, extra }) => (
  <div className="p-2.5 rounded-lg border bg-muted/20">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        {label && <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>}
        <code className="text-xs font-mono break-all block text-foreground leading-relaxed">{str}</code>
        {extra && <p className="text-[10px] text-muted-foreground mt-1 italic">{extra}</p>}
      </div>
      <CopyBtn text={str} label="" />
    </div>
  </div>
);

// Collapsible section wrapper
const Section = ({ title, icon: Icon, iconColor, children, defaultOpen = true, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card data-testid={`sourcing-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className={`h-4 w-4 ${iconColor || 'text-primary'}`} />}
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
          {badge}
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </div>
      {open && <CardContent className="pt-0 pb-4 px-4">{children}</CardContent>}
    </Card>
  );
};

// ===== PLAN DISPLAY =====
export const SourcingPlanDisplay = ({ plan, planId, jobTitle, pdfUrl }) => {
  const p = plan || {};
  const es = p.executive_summary || {};
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!planId) return;
    setDownloading(true);
    try {
      const response = await fetch(pdfUrl || `${BACKEND_URL}/api/sourcing/plan/${planId}/pdf`);
      if (!response.ok) throw new Error('PDF download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Sourcing_Strategy_${(jobTitle || 'Plan').replace(/\s+/g, '_').slice(0, 40)}.pdf`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('PDF downloaded!');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-4" data-testid="sourcing-plan-display">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Smart Sourcing Strategy</h3>
        <div className="flex gap-2">
          {planId && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleDownloadPdf}
              disabled={downloading} data-testid="sourcing-download-pdf-btn">
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Download PDF
            </Button>
          )}
        </div>
      </div>

      {/* Executive Summary */}
      {es.strategy_overview && (
        <Card className="border-primary/20 bg-primary/5" data-testid="sourcing-executive-summary">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <DifficultyBadge rating={es.difficulty_rating} />
              {p.category && <Badge variant="secondary" className="text-xs">{p.category}</Badge>}
              {es.estimated_candidate_pool && <Badge variant="outline" className="text-xs">Pool: {es.estimated_candidate_pool}</Badge>}
              {es.recommended_timeline && <Badge variant="outline" className="text-xs">{es.recommended_timeline}</Badge>}
            </div>
            <p className="text-sm">{es.strategy_overview}</p>
            {es.channel_mix_recommendation && (
              <p className="text-xs text-muted-foreground"><strong>Channel Mix:</strong> {es.channel_mix_recommendation}</p>
            )}
            {es.key_insight && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Lightbulb className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">{es.key_insight}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Primary Channels */}
      {p.primary_channels?.length > 0 && (
        <Section title="Primary Sourcing Channels" icon={Target} iconColor="text-blue-500"
          badge={<Badge variant="secondary" className="text-[10px]">{p.primary_channels.length} channels</Badge>}>
          <div className="space-y-3">
            {p.primary_channels.map((ch, i) => (
              <div key={i} className="p-3 rounded-lg border bg-muted/10">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-sm font-semibold">{ch.name}</span>
                  <CostBadge cost={ch.cost} />
                  {ch.effectiveness && <Badge variant="outline" className="text-[10px]">{ch.effectiveness}</Badge>}
                  {ch.url && (
                    <a href={ch.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                      <ExternalLink className="h-2.5 w-2.5" /> Visit
                    </a>
                  )}
                </div>
                <ul className="space-y-1 mb-2">
                  {(ch.specific_tactics || []).map((t, ti) => (
                    <li key={ti} className="text-xs text-muted-foreground flex items-start gap-1.5">
                      <Zap className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" /><span>{t}</span>
                    </li>
                  ))}
                </ul>
                {ch.boolean_search_example && <SearchBlock str={ch.boolean_search_example} />}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Boolean & X-ray Searches */}
      {(p.boolean_search_strings?.length > 0 || p.xray_search_strings) && (
        <Section title="Search Strings (Boolean & X-Ray)" icon={Search} iconColor="text-orange-500">
          <div className="space-y-3">
            {(p.boolean_search_strings || []).map((b, i) => (
              <SearchBlock key={`bool-${i}`} str={b.search_string} label={`${b.platform}: ${b.purpose}`} />
            ))}
            {p.xray_search_strings && Object.entries(p.xray_search_strings).map(([key, items]) => (
              (items || []).map((item, i) => (
                <SearchBlock key={`${key}-${i}`} str={item.search_string}
                  label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  extra={[item.explanation, item.compliance_note].filter(Boolean).join(' — ')} />
              ))
            ))}
          </div>
        </Section>
      )}

      {/* Network Activation */}
      {p.network_activation && (
        <Section title="Network Activation" icon={Users} iconColor="text-indigo-500">
          <div className="space-y-2">
            {(p.network_activation.referral_tactics || []).map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-xs"><Zap className="h-3 w-3 text-indigo-500 shrink-0 mt-0.5" /><span>{t}</span></div>
            ))}
            {p.network_activation.past_candidate_reengagement && (
              <div className="p-2 rounded bg-indigo-500/5 border border-indigo-500/10 text-xs">{p.network_activation.past_candidate_reengagement}</div>
            )}
            {(p.network_activation.personal_network_plays || []).map((pl, i) => (
              <div key={i} className="flex items-start gap-2 text-xs"><Star className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" /><span>{pl}</span></div>
            ))}
          </div>
        </Section>
      )}

      {/* Alumni Networks */}
      {p.alumni_networks?.length > 0 && (
        <Section title="Alumni Networks" icon={BookOpen} iconColor="text-purple-500">
          <div className="space-y-2">
            {p.alumni_networks.map((a, i) => (
              <div key={i} className="p-2.5 rounded-lg border bg-muted/10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold">{a.name}</span>
                  <Badge variant="secondary" className="text-[10px]">{a.type}</Badge>
                </div>
                <p className="text-[11px] text-muted-foreground">{a.why_relevant}</p>
                {a.access_method && <p className="text-[11px] mt-1"><strong>Access:</strong> {a.access_method}</p>}
                {a.outreach_angle && <p className="text-[11px] text-primary mt-0.5">Hook: {a.outreach_angle}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Local Communities */}
      {p.local_communities?.length > 0 && (
        <Section title="Local Communities" icon={MapPin} iconColor="text-teal-500">
          <div className="space-y-2">
            {p.local_communities.map((c, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border bg-muted/10">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold">{c.name}</span>
                    <Badge variant="secondary" className="text-[10px]">{c.type}</Badge>
                    {c.estimated_size && <span className="text-[10px] text-muted-foreground">{c.estimated_size}</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{c.how_to_engage}</p>
                </div>
                {c.url && (
                  <a href={c.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="sm" className="h-6 text-xs gap-1"><ExternalLink className="h-3 w-3" /></Button>
                  </a>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Social Network Sourcing */}
      {p.social_network_sourcing?.length > 0 && (
        <Section title="Social Network Sourcing" icon={Hash} iconColor="text-pink-500">
          <div className="space-y-2">
            {p.social_network_sourcing.map((s, i) => (
              <div key={i} className="p-2.5 rounded-lg border bg-muted/10">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-pink-500/10 text-pink-600 text-[10px]">{s.platform}</Badge>
                  <span className="text-xs font-medium">{s.where_exactly}</span>
                </div>
                {s.search_or_hashtags && <SearchBlock str={s.search_or_hashtags} />}
                {s.engagement_strategy && <p className="text-[11px] text-muted-foreground mt-1">{s.engagement_strategy}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Open Source & OSINT */}
      {p.open_source_channels?.length > 0 && (
        <Section title="Open Source & OSINT Channels" icon={Globe} iconColor="text-cyan-500">
          <div className="space-y-2">
            {p.open_source_channels.map((o, i) => (
              <div key={i} className="p-2.5 rounded-lg border bg-muted/10">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className="text-[10px]">{o.source}</Badge>
                </div>
                <p className="text-xs">{o.how_to_find_talent}</p>
                {o.signal_of_quality && <p className="text-[11px] text-muted-foreground mt-0.5">Quality signal: {o.signal_of_quality}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Hidden Talent Channels */}
      {p.hidden_talent_channels?.length > 0 && (
        <Section title="Hidden Talent Channels" icon={Coffee} iconColor="text-amber-600"
          badge={<Badge variant="secondary" className="text-[10px]">{p.hidden_talent_channels.length} channels</Badge>}>
          <div className="space-y-2">
            {p.hidden_talent_channels.map((h, i) => (
              <div key={i} className="p-2.5 rounded-lg border bg-muted/10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold">{h.method_name}</span>
                  {h.success_rate && <Badge variant="outline" className="text-[10px]">{h.success_rate}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{h.description}</p>
                {h.access_method && <p className="text-[11px] mt-1"><strong>Access:</strong> {h.access_method}</p>}
                {h.engagement_strategy && <p className="text-[11px] mt-0.5">{h.engagement_strategy}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Niche Communities */}
      {p.niche_communities?.length > 0 && (
        <Section title="Niche Communities" icon={Users} iconColor="text-violet-500">
          <div className="space-y-2">
            {p.niche_communities.map((nc, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border bg-muted/10">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold">{nc.name}</span>
                    <Badge className="bg-violet-500/10 text-violet-600 text-[10px]">{nc.platform}</Badge>
                    {nc.member_count && <span className="text-[10px] text-muted-foreground">{nc.member_count}</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{nc.how_to_join}</p>
                </div>
                {nc.url && (
                  <a href={nc.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="sm" className="h-6 text-xs"><ExternalLink className="h-3 w-3" /></Button>
                  </a>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Job Boards */}
      {p.job_boards && (
        <Section title="Job Boards" icon={BookOpen} iconColor="text-green-500">
          <div className="space-y-3">
            {[
              { key: 'free', label: 'Free Job Boards', color: 'bg-green-500' },
              { key: 'paid', label: 'Paid Job Boards', color: 'bg-amber-500' },
              { key: 'niche', label: 'Niche Job Boards', color: 'bg-violet-500' },
            ].map(({ key, label, color }) => {
              const boards = p.job_boards[key];
              if (!boards?.length) return null;
              return (
                <div key={key}>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">{label}</p>
                  <div className="space-y-1">
                    {boards.map((b, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded border bg-muted/10">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${color}`} />
                          <span className="text-xs font-medium">{b.name}</span>
                          <span className="text-[10px] text-muted-foreground">{b.best_for || b.cost_range || b.specialty}</span>
                        </div>
                        {b.url && (
                          <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline">
                            Visit
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Creative Tactics */}
      {p.creative_tactics?.length > 0 && (
        <Section title="Creative Tactics" icon={Lightbulb} iconColor="text-amber-500">
          <div className="space-y-2">
            {p.creative_tactics.map((ct, i) => (
              <div key={i} className="p-2.5 rounded-lg border bg-muted/10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold">{ct.tactic}</span>
                  <Badge variant="outline" className="text-[10px]">{ct.difficulty}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{ct.description}</p>
                {ct.expected_results && <p className="text-[11px] mt-0.5 text-primary">{ct.expected_results}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Week 1 Action Plan */}
      {p.week_one_action_plan?.length > 0 && (
        <Section title="Week 1 Action Plan" icon={Calendar} iconColor="text-blue-600">
          <div className="space-y-2">
            {p.week_one_action_plan.map((day, i) => (
              <div key={i}>
                <p className="text-xs font-semibold text-primary mb-1">{day.day}</p>
                <ul className="space-y-1 ml-4">
                  {(day.actions || []).map((a, ai) => (
                    <li key={ai} className="text-xs flex items-start gap-1.5">
                      <span className="text-muted-foreground">-</span><span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Pro Tips */}
      {p.pro_tips?.length > 0 && (
        <Section title="Pro Tips" icon={Shield} iconColor="text-emerald-500">
          <div className="space-y-1.5">
            {p.pro_tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <Badge className="bg-emerald-500 text-white text-[10px] shrink-0">{i + 1}</Badge>
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};


// ===== MAIN TAB COMPONENT =====
const SourcingStrategyTab = ({ jobId, jobTitle, jobDescription, jobLocation, jobType, companyName }) => {
  const [status, setStatus] = useState('idle');
  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [progressIdx, setProgressIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    loadExistingReport();
    return () => { clearInterval(pollRef.current); clearInterval(progressRef.current); };
  }, [jobId]);

  const loadExistingReport = async () => {
    try {
      setLoading(true);
      const data = await getSourcingReportByJob(jobId);
      if (data.exists && data.plan) {
        setPlan(data.plan);
        setPlanId(data.plan_id);
        setStatus('completed');
      }
    } catch { /* no existing */ }
    finally { setLoading(false); }
  };

  const startPolling = (mjobId) => {
    clearInterval(pollRef.current);
    let pollCount = 0;
    const MAX_POLLS = 30;
    pollRef.current = setInterval(async () => {
      pollCount++;
      if (pollCount > MAX_POLLS) {
        setStatus('failed');
        clearInterval(pollRef.current);
        clearInterval(progressRef.current);
        toast.error('Generation timed out — please try again');
        return;
      }
      try {
        const s = await getSourcingJobStatus(mjobId);
        if (s.status === 'completed') {
          setPlanId(s.plan_id);
          const data = await getSourcingReportByJob(jobId);
          if (data.plan) setPlan(data.plan);
          setStatus('completed');
          clearInterval(pollRef.current);
          clearInterval(progressRef.current);
        } else if (s.status === 'failed') {
          setStatus('failed');
          clearInterval(pollRef.current);
          clearInterval(progressRef.current);
          toast.error(s.error || 'Sourcing plan generation failed');
        }
      } catch { /* continue polling */ }
    }, 4000);
  };

  const handleGenerate = async () => {
    if (!jobDescription) {
      toast.error('Job description is required to generate a sourcing plan');
      return;
    }
    setStatus('generating');
    setProgressIdx(0);
    progressRef.current = setInterval(() => {
      setProgressIdx(prev => (prev + 1) % PROGRESS_MESSAGES.length);
    }, 4000);

    try {
      const res = await generateSourcingPlan({
        job_title: jobTitle,
        job_description: jobDescription,
        job_location: jobLocation || 'United States',
        work_type: jobType === 'remote' ? 'Remote' : jobType === 'hybrid' ? 'Hybrid' : 'Onsite',
        company_name: companyName || '',
        hirefast_job_id: jobId,
      });
      startPolling(res.job_id);
    } catch {
      setStatus('failed');
      clearInterval(progressRef.current);
      toast.error('Failed to start sourcing plan generation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (status === 'generating') {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-6" data-testid="sourcing-generating">
        <div className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-orange-500/20 border-t-orange-500 animate-spin" />
          <Search className="h-8 w-8 text-orange-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Building Sourcing Strategy</h3>
          <p className="text-sm text-muted-foreground animate-pulse max-w-md">{PROGRESS_MESSAGES[progressIdx]}</p>
        </div>
        <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-orange-500 rounded-full animate-pulse" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  if (status === 'completed' && plan) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleGenerate} data-testid="sourcing-regenerate-btn">
            <RefreshCw className="h-3.5 w-3.5" /> Regenerate Strategy
          </Button>
        </div>
        <SourcingPlanDisplay plan={plan} planId={planId} jobTitle={jobTitle} />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 space-y-6" data-testid="sourcing-empty-state">
      <div className="w-20 h-20 rounded-full bg-orange-500/10 flex items-center justify-center">
        <Search className="h-10 w-10 text-orange-500" />
      </div>
      <div className="text-center space-y-2 max-w-md">
        <h3 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Smart Sourcing AI</h3>
        <p className="text-sm text-muted-foreground">
          Generate a comprehensive sourcing strategy covering every channel — professional networks, alumni groups,
          local communities, niche job boards, social networks, and ready-to-copy Boolean & X-ray search strings.
        </p>
      </div>
      {status === 'failed' && (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="h-3 w-3" /> Generation failed — please try again
        </Badge>
      )}
      <Button onClick={handleGenerate} className="gap-2" size="lg" data-testid="sourcing-generate-btn">
        <Zap className="h-4 w-4" /> Generate Sourcing Strategy
      </Button>
    </div>
  );
};

export default SourcingStrategyTab;
